import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import logoSvg from '../../assets/logo.svg';
import './Home.css';

/* ─── Component ─────────────────────────────────────────── */

const Home: React.FC = () => {
  const { t } = useLanguage();
  const { home } = t;

  return (
    <div className="home">

      {/* ── Hero ─────────────────────────────────────── */}
      <section className="hero">
        <div className="hero__bg-overlay" />
        {/* Logo watermark */}
        <img src={logoSvg} alt="" className="hero__watermark" aria-hidden="true" />
        <div className="hero__particles" aria-hidden="true">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} className="hero__particle" style={{
              '--delay': `${(i * 0.4) % 5}s`,
              '--x': `${(i * 17 + 5) % 95}%`,
              '--size': `${4 + (i % 5) * 3}px`,
            } as React.CSSProperties} />
          ))}
        </div>
        <div className="hero__content">
          <div className="hero__badge">{home.heroBadge}</div>
          <h1 className="hero__headline">
            {home.heroHeadline1} <span className="hero__headline-accent">{home.heroHeadlineAccent}</span><br />
            {home.heroHeadline2}
          </h1>
          <p className="hero__subheadline">{home.heroSub}</p>
          <div className="hero__actions">
            <Link to="/contact" className="btn btn--primary btn--lg">
              {home.heroStartProject}
            </Link>
            <Link to="/about" className="btn btn--outline-white btn--lg">
              {home.heroLearnMore}
            </Link>
          </div>
          <div className="hero__stats">
            <div className="hero__stat">
              <span className="hero__stat-number">7+</span>
              <span className="hero__stat-label">{home.heroStatService}</span>
            </div>
            <div className="hero__stat-divider" />
            <div className="hero__stat">
              <span className="hero__stat-number">360°</span>
              <span className="hero__stat-label">{home.heroStatCoverage}</span>
            </div>
            <div className="hero__stat-divider" />
            <div className="hero__stat">
              <span className="hero__stat-number">AI</span>
              <span className="hero__stat-label">{home.heroStatInsights}</span>
            </div>
          </div>
        </div>
        <div className="hero__scroll-indicator" aria-hidden="true">
          <span />
        </div>
      </section>

      {/* ── Who We Are ───────────────────────────────── */}
      <section className="who-we-are section">
        <div className="container">
          <div className="who-we-are__grid">
            <div className="who-we-are__visual">
              <div className="who-we-are__card-stack">
                <div className="who-we-are__card who-we-are__card--back" />
                <div className="who-we-are__card who-we-are__card--front">
                  <div className="who-we-are__icon-grid">
                    <span>🔬</span><span>💊</span>
                    <span>🧬</span><span>🩺</span>
                  </div>
                  <p>{home.whoCardSub}</p>
                </div>
              </div>
            </div>
            <div className="who-we-are__content">
              <div className="section-label">{home.whoLabel}</div>
              <h2 className="section-title">
                {home.whoTitle1}<br />
                <span className="text-accent">{home.whoTitleAccent}</span>
              </h2>
              <p className="section-body">{home.whoBody1}</p>
              <p className="section-body">{home.whoBody2}</p>
              <Link to="/about" className="btn btn--primary">
                {home.whoBtn}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── What We Do ───────────────────────────────── */}
      <section className="services section section--alt">
        <div className="container">
          <div className="section-header">
            <div className="section-label">{home.servicesLabel}</div>
            <h2 className="section-title">
              {home.servicesTitle1}<br />
              <span className="text-accent">{home.servicesTitleAccent}</span>
            </h2>
            <p className="section-subtitle">{home.servicesSub}</p>
          </div>
          <div className="services__grid">
            {home.services.map((s, i) => (
              <div className="service-card" key={i}>
                <div className="service-card__icon">{s.icon}</div>
                <h3 className="service-card__title">{s.title}</h3>
                <p className="service-card__desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who We Serve ─────────────────────────────── */}
      <section className="audience section">
        <div className="container">
          <div className="section-header">
            <div className="section-label">{home.audienceLabel}</div>
            <h2 className="section-title">
              {home.audienceTitle1} <span className="text-accent">{home.audienceTitleAccent}</span>
            </h2>
            <p className="section-subtitle">{home.audienceSub}</p>
          </div>
          <div className="audience__grid">
            {home.audiences.map((a, i) => (
              <div className="audience-card" key={i}>
                <div className="audience-card__icon">{a.icon}</div>
                <div className="audience-card__content">
                  <h3 className="audience-card__title">{a.title}</h3>
                  <p className="audience-card__desc">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── G-Medix Feature ──────────────────────────── */}
      <section className="gmedix section section--dark">
        <div className="container">
          <div className="gmedix__grid">
            <div className="gmedix__content">
              <div className="section-label section-label--light">{home.gmedixLabel}</div>
              <h2 className="section-title section-title--light">G-Medix</h2>
              <p className="section-body section-body--light">{home.gmedixBody}</p>
              <ul className="gmedix__phases">
                <li>
                  <span className="gmedix__phase-badge">Phase 1</span>
                  {home.gmedixPhase1}
                </li>
                <li>
                  <span className="gmedix__phase-badge">Phase 2</span>
                  {home.gmedixPhase2}
                </li>
              </ul>
              <Link to="/about" className="btn btn--secondary">
                {home.gmedixBtn}
              </Link>
            </div>
            <div className="gmedix__visual">
              <div className="gmedix__pulse-rings" aria-hidden="true">
                <div className="gmedix__ring gmedix__ring--1" />
                <div className="gmedix__ring gmedix__ring--2" />
                <div className="gmedix__ring gmedix__ring--3" />
                <div className="gmedix__core">
                  <span className="gmedix__core-icon">❤️</span>
                  <span className="gmedix__core-label">Vascular Health</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Impact ─────────────────────────────── */}
      <section className="social-impact section section--alt">
        <div className="container">
          <div className="section-header">
            <div className="section-label">{home.impactLabel}</div>
            <h2 className="section-title">
              {home.impactTitle1}<br />
              <span className="text-accent">{home.impactTitleAccent}</span>
            </h2>
          </div>
          <div className="social-impact__grid">
            <div className="social-impact__card">
              <div className="social-impact__card-icon">🤝</div>
              <h3>{home.impactCard1Title}</h3>
              <p>{home.impactCard1Body}</p>
            </div>
            <div className="social-impact__card">
              <div className="social-impact__card-icon">🌍</div>
              <h3>{home.impactCard2Title}</h3>
              <p>{home.impactCard2Body}</p>
            </div>
            <div className="social-impact__card">
              <div className="social-impact__card-icon">🎯</div>
              <h3>{home.impactCard3Title}</h3>
              <p>{home.impactCard3Body}</p>
              <a href="mailto:socialimpact@mahmedix.com" className="social-impact__email">
                socialimpact@mahmedix.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────── */}
      <section className="cta-banner">
        <div className="cta-banner__overlay" />
        <div className="container cta-banner__content">
          <h2 className="cta-banner__headline">{home.ctaTitle}</h2>
          <p className="cta-banner__body">{home.ctaBody}</p>
          <Link to="/contact" className="btn btn--secondary btn--lg">
            {home.ctaBtn}
          </Link>
        </div>
      </section>

    </div>
  );
};

export default Home;
